import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { User } from '@/types/auth';
import { rpc, setAuthToken } from '@/api/client';
import { getGoogleClientId } from '@/lib/config';
import { redirectToGoogleAuth } from '@shared/auth/oauth';
import { setErrorReporterUser } from '@shared/error-reporter';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signIn: () => void;
  signOut: () => void;
  refreshUser: () => Promise<void>;
  handleCredentialResponse: (response: { credential: string; name?: string }) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'auth-token';
const TOKEN_EXPIRES_AT_KEY = 'auth-token-expires-at';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearLocalSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
    setAuthToken(null);
    setUser(null);
    setErrorReporterUser(null);
    window.location.hash = '#/login';
  }, []);

  const signOut = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && token.startsWith('jt_at_')) {
      void fetch('/rpc/RevokeSessionToken', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        keepalive: true,
        body: '{}',
      }).catch(() => {});
    }
    clearLocalSession();
  }, [clearLocalSession]);

  const refreshUser = useCallback(async () => {
    let token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      setAuthToken(token);
      try {
        const currentUser = await rpc<User>('GetCurrentUser');
        setUser(currentUser);
        setErrorReporterUser({ id: currentUser.id });
      } catch (err) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthToken(null);
        setErrorReporterUser(null);
      }
    }
  }, []);

  useEffect(() => {
    const handleSignOutEvent = () => clearLocalSession();
    const handleTermsRequiredEvent = () => { refreshUser(); };
    window.addEventListener('auth:signout', handleSignOutEvent);
    window.addEventListener('auth:terms-required', handleTermsRequiredEvent);
    return () => {
      window.removeEventListener('auth:signout', handleSignOutEvent);
      window.removeEventListener('auth:terms-required', handleTermsRequiredEvent);
    };
  }, [clearLocalSession, refreshUser]);

  useEffect(() => {
    async function loadUser() {
      await refreshUser();
      setIsLoading(false);
    }
    loadUser();
  }, [refreshUser]);

  const handleCredentialResponse = useCallback(async (response: { credential: string; name?: string }) => {
    setIsLoading(true);
    let token = response.credential;
    localStorage.setItem(TOKEN_KEY, token);
    setAuthToken(token);
    try {
      try {
        const session = await rpc<{ token: string; expiresIn: number; expiresAt: string }>('ExchangeSessionToken', {
          clientId: 'telemetry-editor',
          clientName: 'Telemetry Editor',
          ...(response.name ? { name: response.name } : {}),
        });
        if (session && session.token) {
          token = session.token;
          localStorage.setItem(TOKEN_KEY, token);
          if (session.expiresAt) {
            localStorage.setItem(TOKEN_EXPIRES_AT_KEY, new Date(session.expiresAt).getTime().toString());
          }
          setAuthToken(token);
        }
      } catch (e) {
        console.warn('Session token exchange failed:', e);
      }
      const currentUser = await rpc<User>('GetCurrentUser');
      setUser(currentUser);
      setErrorReporterUser({ id: currentUser.id });
    } catch (err) {
      console.error('Failed to get user after sign in', err);
      signOut();
    } finally {
      setIsLoading(false);
    }
  }, [signOut]);

  const signIn = useCallback(() => {
    window.location.hash = '#/login';
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, signIn, signOut, refreshUser, handleCredentialResponse }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
